import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { Link } from 'react-router-dom';
import { RankBadge } from '../components/RankBadge';
import { useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { formatAddress } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
    Users, 
    Crown,
    UserPlus,
    UserMinus,
    Shield,
    Trophy,
    Plus,
    Search,
    Loader2,
    LogOut,
    ArrowRightLeft,
    Trash2,
    Zap,
    Medal,
    Star,
    MessageCircle
} from 'lucide-react';
import { TOURNAMENT_CONTRACT_ADDRESS, TOURNAMENT_CONTRACT_ABI } from '../contracts/tournamentConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Team Card Component
function TeamCard({ team, currentAddress, onViewTeam }) {
    const isMember = team.members?.includes(currentAddress?.toLowerCase());
    const isCaptain = team.captain === currentAddress?.toLowerCase();
    
    return (
        <Card 
            className={`bg-white/[0.02] border-white/5 hover:border-[#9B6FE8]/30 transition-all cursor-pointer card-hover ${isMember ? 'border-[#00FF66]/30' : ''}`}
            onClick={() => onViewTeam(team)}
            data-testid={`team-card-${team.id}`}
        >
            <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#9B6FE8]/20 to-[#6F3FCA]/20 flex items-center justify-center">
                            <Users className="w-6 h-6 text-[#9B6FE8]" />
                        </div>
                        <div>
                            <h3 className="text-white font-semibold">{team.name}</h3>
                            <p className="text-white/40 text-sm">
                                {team.member_count || team.members?.length || 0} members
                            </p>
                        </div>
                    </div>
                    {isCaptain && (
                        <Badge className="bg-[#FFD700]/20 text-[#FFD700]">
                            <Crown className="w-3 h-3 mr-1" />
                            Captain
                        </Badge>
                    )}
                    {isMember && !isCaptain && (
                        <Badge className="bg-[#00FF66]/20 text-[#00FF66]">
                            Member
                        </Badge>
                    )}
                </div>
                
                {team.description && (
                    <p className="text-white/50 text-sm mb-3 line-clamp-2">{team.description}</p>
                )}
                
                <div className="flex items-center gap-4 text-xs text-white/40">
                    <span className="flex items-center gap-1">
                        <Trophy className="w-3 h-3" />
                        {team.stats?.tournaments_won || 0} wins
                    </span>
                    <span className="flex items-center gap-1">
                        <Medal className="w-3 h-3" />
                        {team.stats?.tournaments_joined || 0} tournaments
                    </span>
                    {team.on_chain_id && (
                        <span className="flex items-center gap-1 text-[#FFD700]">
                            <Zap className="w-3 h-3" />
                            On-chain
                        </span>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

// Create Team Modal
function CreateTeamModal({ isOpen, onClose, onSuccess, address }) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    
    const handleCreate = async () => {
        if (!name.trim()) {
            toast.error('Team name is required');
            return;
        }
        
        setLoading(true);
        try {
            const response = await fetch(
                `${API_URL}/api/teams?name=${encodeURIComponent(name)}&captain=${address}&description=${encodeURIComponent(description)}`,
                { method: 'POST' }
            );
            const result = await response.json();
            
            if (result.success) {
                toast.success('Team created successfully!');
                onSuccess();
                onClose();
                setName('');
                setDescription('');
            } else {
                toast.error(result.error || 'Failed to create team');
            }
        } catch (error) {
            toast.error('Failed to create team');
        } finally {
            setLoading(false);
        }
    };
    
    if (!isOpen) return null;
    
    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="bg-[#1A0B2E] border-[#9B6FE8]/30 max-w-md w-full">
                <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                        <Plus className="w-5 h-5 text-[#00FF66]" />
                        Create New Team
                    </CardTitle>
                    <CardDescription className="text-white/50">
                        Start a team and invite members to compete in tournaments together
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div>
                        <label className="text-sm text-white/60 mb-2 block">Team Name *</label>
                        <Input
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="Alpha Bettors"
                            className="bg-white/5 border-white/10"
                            data-testid="team-name-input"
                        />
                    </div>
                    <div>
                        <label className="text-sm text-white/60 mb-2 block">Description</label>
                        <Input
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="The best betting team on Base!"
                            className="bg-white/5 border-white/10"
                        />
                    </div>
                    
                    <div className="flex gap-3 pt-2">
                        <Button 
                            variant="outline" 
                            className="flex-1 border-white/10"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                        <Button 
                            className="flex-1 bg-[#00FF66] text-[#0A0A0C]"
                            onClick={handleCreate}
                            disabled={loading}
                            data-testid="create-team-submit"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Team'}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

// Team Detail Modal
function TeamDetailModal({ team, isOpen, onClose, onRefresh, currentAddress }) {
    const [newMember, setNewMember] = useState('');
    const [loading, setLoading] = useState(false);
    
    if (!isOpen || !team) return null;
    
    const isCaptain = team.captain === currentAddress?.toLowerCase();
    const isMember = team.members?.includes(currentAddress?.toLowerCase());
    
    const handleAddMember = async () => {
        if (!newMember.trim() || !newMember.startsWith('0x')) {
            toast.error('Enter a valid wallet address');
            return;
        }
        
        setLoading(true);
        try {
            const response = await fetch(
                `${API_URL}/api/teams/${team.id}/members?captain=${currentAddress}&member=${newMember}`,
                { method: 'POST' }
            );
            const result = await response.json();
            
            if (result.success) {
                toast.success('Member added!');
                setNewMember('');
                onRefresh();
            } else {
                toast.error(result.error || 'Failed to add member');
            }
        } catch (error) {
            toast.error('Failed to add member');
        } finally {
            setLoading(false);
        }
    };
    
    const handleRemoveMember = async (member) => {
        setLoading(true);
        try {
            const response = await fetch(
                `${API_URL}/api/teams/${team.id}/members/${member}?captain=${currentAddress}`,
                { method: 'DELETE' }
            );
            const result = await response.json();
            
            if (result.success) {
                toast.success('Member removed');
                onRefresh();
            } else {
                toast.error(result.error || 'Failed to remove member');
            }
        } catch (error) {
            toast.error('Failed to remove member');
        } finally {
            setLoading(false);
        }
    };
    
    const handleLeaveTeam = async () => {
        setLoading(true);
        try {
            const response = await fetch(
                `${API_URL}/api/teams/${team.id}/leave?member=${currentAddress}`,
                { method: 'POST' }
            );
            const result = await response.json();
            
            if (result.success) {
                toast.success('Left team successfully');
                onRefresh();
                onClose();
            } else {
                toast.error(result.error || 'Failed to leave team');
            }
        } catch (error) {
            toast.error('Failed to leave team');
        } finally {
            setLoading(false);
        }
    };
    
    const handleDisbandTeam = async () => {
        if (!confirm('Are you sure you want to disband this team? This action cannot be undone.')) {
            return;
        }
        
        setLoading(true);
        try {
            const response = await fetch(
                `${API_URL}/api/teams/${team.id}?captain=${currentAddress}`,
                { method: 'DELETE' }
            );
            const result = await response.json();
            
            if (result.success) {
                toast.success('Team disbanded');
                onRefresh();
                onClose();
            } else {
                toast.error(result.error || 'Failed to disband team');
            }
        } catch (error) {
            toast.error('Failed to disband team');
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="bg-[#1A0B2E] border-[#9B6FE8]/30 max-w-lg w-full max-h-[80vh] overflow-y-auto">
                <CardHeader>
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#9B6FE8]/20 to-[#6F3FCA]/20 flex items-center justify-center">
                                <Users className="w-7 h-7 text-[#9B6FE8]" />
                            </div>
                            <div>
                                <CardTitle className="text-white">{team.name}</CardTitle>
                                <CardDescription className="text-white/50">
                                    {team.members?.length || 0} members
                                </CardDescription>
                            </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={onClose} className="text-white/50">
                            ✕
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Team Stats */}
                    <div className="grid grid-cols-3 gap-3">
                        <div className="text-center p-3 rounded-lg bg-white/[0.03]">
                            <Trophy className="w-5 h-5 text-[#FFD700] mx-auto mb-1" />
                            <div className="text-white font-bold">{team.stats?.tournaments_won || 0}</div>
                            <div className="text-xs text-white/40">Wins</div>
                        </div>
                        <div className="text-center p-3 rounded-lg bg-white/[0.03]">
                            <Medal className="w-5 h-5 text-[#9B6FE8] mx-auto mb-1" />
                            <div className="text-white font-bold">{team.stats?.tournaments_joined || 0}</div>
                            <div className="text-xs text-white/40">Tournaments</div>
                        </div>
                        <div className="text-center p-3 rounded-lg bg-white/[0.03]">
                            <Star className="w-5 h-5 text-[#00FF66] mx-auto mb-1" />
                            <div className="text-white font-bold">{team.stats?.total_earnings || 0}</div>
                            <div className="text-xs text-white/40">ETH Won</div>
                        </div>
                    </div>
                    
                    {/* Members List */}
                    <div>
                        <h4 className="text-sm text-white/60 mb-2">Team Members</h4>
                        <div className="space-y-2">
                            {team.members?.map((member, idx) => (
                                <div 
                                    key={member} 
                                    className="flex items-center justify-between p-3 rounded-lg bg-white/[0.03]"
                                >
                                    <div className="flex items-center gap-2">
                                        {member === team.captain ? (
                                            <Crown className="w-4 h-4 text-[#FFD700]" />
                                        ) : (
                                            <Users className="w-4 h-4 text-white/40" />
                                        )}
                                        <span className="text-white font-mono text-sm">
                                            {formatAddress(member)}
                                        </span>
                                        {member === team.captain && (
                                            <Badge className="bg-[#FFD700]/20 text-[#FFD700] text-xs">Captain</Badge>
                                        )}
                                    </div>
                                    {isCaptain && member !== team.captain && (
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="text-red-400 hover:text-red-300"
                                            onClick={() => handleRemoveMember(member)}
                                        >
                                            <UserMinus className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    {/* Add Member (Captain only) */}
                    {isCaptain && (
                        <div className="p-4 rounded-lg bg-white/[0.03] border border-white/5">
                            <h4 className="text-sm text-white/60 mb-2">Add New Member</h4>
                            <div className="flex gap-2">
                                <Input
                                    value={newMember}
                                    onChange={e => setNewMember(e.target.value)}
                                    placeholder="0x..."
                                    className="bg-white/5 border-white/10 font-mono text-sm"
                                    data-testid="add-member-input"
                                />
                                <Button 
                                    className="bg-[#00FF66] text-[#0A0A0C]"
                                    onClick={handleAddMember}
                                    disabled={loading}
                                    data-testid="add-member-button"
                                >
                                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                                </Button>
                            </div>
                        </div>
                    )}
                    
                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                        {isMember && !isCaptain && (
                            <Button 
                                variant="outline" 
                                className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                                onClick={handleLeaveTeam}
                                disabled={loading}
                            >
                                <LogOut className="w-4 h-4 mr-2" />
                                Leave Team
                            </Button>
                        )}
                        {isCaptain && (
                            <Button 
                                variant="outline" 
                                className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10"
                                onClick={handleDisbandTeam}
                                disabled={loading}
                            >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Disband Team
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export function TeamsPage() {
    const { address, isConnected } = useWeb3();
    const [teams, setTeams] = useState([]);
    const [myTeam, setMyTeam] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    
    useEffect(() => {
        fetchTeams();
        fetchLeaderboard();
    }, []);
    
    useEffect(() => {
        if (address) {
            fetchMyTeam();
        }
    }, [address]);
    
    const fetchTeams = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/teams?limit=50`);
            if (response.ok) {
                const data = await response.json();
                setTeams(data);
            }
        } catch (error) {
            console.error('Failed to fetch teams:', error);
        } finally {
            setLoading(false);
        }
    };
    
    const fetchMyTeam = async () => {
        if (!address) return;
        try {
            const response = await fetch(`${API_URL}/api/teams/user/${address}`);
            if (response.ok) {
                const data = await response.json();
                setMyTeam(data.team || data);
            }
        } catch (error) {
            console.error('Failed to fetch user team:', error);
        }
    };
    
    const fetchLeaderboard = async () => {
        try {
            const response = await fetch(`${API_URL}/api/teams/leaderboard?limit=10`);
            if (response.ok) {
                const data = await response.json();
                setLeaderboard(data);
            }
        } catch (error) {
            console.error('Failed to fetch leaderboard:', error);
        }
    };
    
    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            fetchTeams();
            return;
        }
        
        try {
            const response = await fetch(`${API_URL}/api/teams/search?q=${encodeURIComponent(searchQuery)}`);
            if (response.ok) {
                const data = await response.json();
                setTeams(data);
            }
        } catch (error) {
            console.error('Failed to search teams:', error);
        }
    };
    
    const handleRefresh = () => {
        fetchTeams();
        fetchMyTeam();
        fetchLeaderboard();
        if (selectedTeam) {
            // Refresh selected team details
            fetch(`${API_URL}/api/teams/${selectedTeam.id}`)
                .then(res => res.json())
                .then(data => setSelectedTeam(data))
                .catch(() => {});
        }
    };
    
    const filteredTeams = teams.filter(team => 
        !searchQuery || team.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 page-enter" data-testid="teams-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 animate-fade-in-down">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#9B6FE8]/20 to-[#6F3FCA]/20 flex items-center justify-center">
                        <Users className="w-7 h-7 text-[#9B6FE8]" />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-white">Teams</h1>
                        <p className="text-white/50 text-sm">Create or join a team to compete in tournaments</p>
                    </div>
                </div>
                
                {isConnected && !myTeam && (
                    <Button 
                        className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] btn-press"
                        onClick={() => setShowCreateModal(true)}
                        data-testid="create-team-button"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Team
                    </Button>
                )}
            </div>
            
            {/* My Team Card */}
            {myTeam && myTeam.id && (
                <Card className="bg-gradient-to-r from-[#9B6FE8]/10 to-[#00FF66]/10 border-[#9B6FE8]/30 mb-8 animate-fade-in-up delay-1">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#9B6FE8]/30 to-[#6F3FCA]/30 flex items-center justify-center">
                                    <Users className="w-8 h-8 text-[#9B6FE8]" />
                                </div>
                                <div>
                                    <div className="text-white/50 text-sm mb-1">Your Team</div>
                                    <h2 className="text-xl font-bold text-white">{myTeam.name}</h2>
                                    <div className="flex items-center gap-2 mt-1">
                                        {myTeam.is_captain && (
                                            <Badge className="bg-[#FFD700]/20 text-[#FFD700]">
                                                <Crown className="w-3 h-3 mr-1" />
                                                Captain
                                            </Badge>
                                        )}
                                        <span className="text-white/40 text-sm">
                                            {myTeam.members?.length || 0} members
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Link to={`/teams/${myTeam.id}/chat`}>
                                    <Button 
                                        className="bg-[#9B6FE8] hover:bg-[#8B5FD8]"
                                        data-testid="team-chat-btn"
                                    >
                                        <MessageCircle className="w-4 h-4 mr-2" />
                                        Chat
                                    </Button>
                                </Link>
                                <Button 
                                    variant="outline" 
                                    className="border-white/10"
                                    onClick={() => setSelectedTeam(myTeam)}
                                >
                                    Manage Team
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Teams List */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Search */}
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                            <Input
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                placeholder="Search teams..."
                                className="pl-10 bg-white/5 border-white/10"
                                data-testid="team-search-input"
                            />
                        </div>
                        <Button variant="outline" className="border-white/10" onClick={handleSearch}>
                            Search
                        </Button>
                    </div>
                    
                    {/* Teams Grid */}
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[1, 2, 3, 4].map(i => (
                                <Skeleton key={i} className="h-40 bg-white/5" />
                            ))}
                        </div>
                    ) : filteredTeams.length === 0 ? (
                        <Card className="bg-white/[0.02] border-white/5">
                            <CardContent className="p-12 text-center">
                                <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                <h3 className="text-xl font-semibold text-white mb-2">No Teams Found</h3>
                                <p className="text-white/50">
                                    {searchQuery ? 'Try a different search term' : 'Be the first to create a team!'}
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {filteredTeams.map(team => (
                                <TeamCard 
                                    key={team.id} 
                                    team={team} 
                                    currentAddress={address}
                                    onViewTeam={setSelectedTeam}
                                />
                            ))}
                        </div>
                    )}
                </div>
                
                {/* Leaderboard Sidebar */}
                <div className="space-y-6">
                    <Card className="bg-white/[0.02] border-white/5">
                        <CardHeader>
                            <CardTitle className="text-lg text-white flex items-center gap-2">
                                <Trophy className="w-5 h-5 text-[#FFD700]" />
                                Top Teams
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {leaderboard.length === 0 ? (
                                <p className="text-white/40 text-center py-4">No teams yet</p>
                            ) : (
                                <div className="space-y-3">
                                    {leaderboard.slice(0, 5).map((team, idx) => {
                                        return (
                                            <div 
                                                key={team.id}
                                                className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer"
                                                onClick={() => setSelectedTeam(team)}
                                            >
                                                <span className="w-6 flex justify-center">
                                                    {idx < 3 ? <RankBadge rank={idx + 1} size="sm" /> : <span className="font-mono text-white/50 text-sm">#{idx + 1}</span>}
                                                </span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-white text-sm font-medium truncate">
                                                        {team.name}
                                                    </div>
                                                    <div className="text-white/40 text-xs">
                                                        {team.stats?.tournaments_won || 0} wins
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    
                    {/* Info Card */}
                    <Card className="bg-white/[0.02] border-white/5">
                        <CardContent className="p-4">
                            <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                                <Shield className="w-4 h-4 text-[#00FF66]" />
                                Team Benefits
                            </h4>
                            <ul className="text-white/50 text-sm space-y-2">
                                <li>• Compete in team tournaments</li>
                                <li>• Share prize pools with members</li>
                                <li>• Build reputation together</li>
                                <li>• On-chain registration available</li>
                            </ul>
                        </CardContent>
                    </Card>
                </div>
            </div>
            
            {/* Modals */}
            <CreateTeamModal 
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={handleRefresh}
                address={address}
            />
            
            <TeamDetailModal
                team={selectedTeam}
                isOpen={!!selectedTeam}
                onClose={() => setSelectedTeam(null)}
                onRefresh={handleRefresh}
                currentAddress={address}
            />
        </div>
    );
}
